import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Presentation, 
  Upload, 
  FileText, 
  ArrowRight, 
  AlertCircle, 
  CheckCircle, 
  Loader2,
  ChevronLeft,
  ChevronRight,
  Play,
  RefreshCw,
  Maximize,
  ZoomIn,
  ZoomOut,
  Download,
  ExternalLink
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CreateSession() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    user_id: "",
    topic_name: "",
    concept_overview: "",
    notes: ""
  });
  const [presentationFile, setPresentationFile] = useState(null);
  
  // Process state
  const [step, setStep] = useState(1); // 1: Form, 2: Processing, 3: Preview
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [extractionStatus, setExtractionStatus] = useState('idle'); // idle, uploading, extracting, done, failed
  
  // PDF viewing state
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfScale, setPdfScale] = useState(1.0);
  const [fileType, setFileType] = useState(null);
  const [originalFileUrl, setOriginalFileUrl] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null); // URL for PDF viewing (original or converted)
  const [viewMode, setViewMode] = useState('pdf'); // 'pdf', 'images', 'external'

  const [replacingFile, setReplacingFile] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
      if (response.data.length > 0) {
        setFormData(prev => ({ ...prev, user_id: response.data[0].id }));
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['.ppt', '.pptx', '.pdf', '.key', '.odp', '.odf', '.pptm'];
      const fileExt = '.' + file.name.split('.').pop().toLowerCase();
      if (!validTypes.includes(fileExt)) {
        toast.error("Please upload a valid presentation file (PPT, PPTX, PDF, KEY, ODP, or ODF)");
        return;
      }
      setPresentationFile(file);
      // Reset if changing file
      setSlides([]);
      setExtractionStatus('idle');
    }
  };

  // Replace presentation file for existing session
  const handleReplaceFile = async (e) => {
    const file = e.target.files[0];
    if (!file || !sessionId) return;

    const validTypes = ['.ppt', '.pptx', '.pdf', '.key', '.odp', '.odf', '.pptm'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    if (!validTypes.includes(fileExt)) {
      toast.error("Please upload a valid presentation file");
      return;
    }

    setReplacingFile(true);
    setPresentationFile(file);
    setSlides([]);

    try {
      const fileFormData = new FormData();
      fileFormData.append('file', file);
      
      await axios.post(`${API}/sessions/${sessionId}/upload-presentation`, fileFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Fetch new slides
      const slidesResponse = await axios.get(`${API}/sessions/${sessionId}/slides`);
      
      if (slidesResponse.data.slides && slidesResponse.data.slides.length > 0) {
        setSlides(slidesResponse.data.slides);
        setCurrentSlide(0);
        toast.success(`Presentation replaced! ${slidesResponse.data.total} slides ready.`);
      } else {
        toast.warning("Slide preview not available for this file format.");
      }
    } catch (error) {
      console.error("Replace file failed:", error);
      toast.error(error.response?.data?.detail || "Failed to replace presentation");
    } finally {
      setReplacingFile(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.user_id) {
      toast.error("Please select a user");
      return;
    }
    
    if (!formData.topic_name) {
      toast.error("Please enter a topic name");
      return;
    }

    if (!presentationFile) {
      toast.error("Please upload a presentation file");
      return;
    }

    // Move to processing step
    setStep(2);
    setLoading(true);
    setExtractionStatus('uploading');

    try {
      // Step 1: Create session
      const sessionResponse = await axios.post(`${API}/sessions`, formData);
      const newSessionId = sessionResponse.data.id;
      setSessionId(newSessionId);

      // Step 2: Upload presentation file
      toast.info("Uploading presentation...");
      setExtractionStatus('extracting');
      const fileFormData = new FormData();
      fileFormData.append('file', presentationFile);
      
      // Upload returns immediately, processing happens in background
      const uploadResponse = await axios.post(`${API}/sessions/${newSessionId}/upload-presentation`, fileFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000 // 60 second timeout for upload only
      });

      console.log("Upload response:", uploadResponse.data);
      toast.info("Processing presentation... This may take a moment.");

      // Step 3: Poll for processing completion (up to 120 seconds)
      let processingComplete = false;
      let pollAttempts = 0;
      const maxAttempts = 60; // 60 attempts * 2 seconds = 120 seconds max wait
      let stuckInProcessing = 0; // Track how long we've been in "processing" state
      
      while (!processingComplete && pollAttempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds between polls
        
        try {
          // First check processing status
          const statusResponse = await axios.get(`${API}/sessions/${newSessionId}/processing-status`);
          console.log(`Processing status (attempt ${pollAttempts + 1}):`, statusResponse.data);
          
          const { status, slide_count, has_pdf, error } = statusResponse.data;
          
          // Check if we're stuck in "processing" state (background task might not be running)
          if (status === 'processing') {
            stuckInProcessing++;
            if (stuckInProcessing >= 10) { // After 20 seconds of no progress
              console.log("Stuck in processing state, triggering manual retry...");
              toast.info("Processing seems stuck, retrying...");
              try {
                await axios.post(`${API}/sessions/${newSessionId}/retry-processing`);
                stuckInProcessing = 0; // Reset counter after retry
              } catch (retryErr) {
                console.log("Retry failed:", retryErr);
              }
            }
          } else {
            stuckInProcessing = 0; // Reset if we're in a different state
          }
          
          if (status === 'complete' || status === 'failed') {
            processingComplete = true;
            
            if (status === 'failed') {
              // Provide helpful error message
              let errorMsg = error || 'Unknown error';
              if (errorMsg.includes('No conversion service configured')) {
                errorMsg = 'PowerPoint conversion not configured. Please ask your admin to set up ConvertAPI in Admin > Conversion Settings.';
              }
              toast.error(errorMsg, { duration: 10000 });
              setExtractionStatus('error');
              setStep(3); // Move to step 3 so user can see error state
              setLoading(false);
              break;
            }
          }
          
          // Also check for slides directly
          const slidesResponse = await axios.get(`${API}/sessions/${newSessionId}/slides`);
          console.log(`Slides check (attempt ${pollAttempts + 1}):`, slidesResponse.data);
          
          if (slidesResponse.data.slides && slidesResponse.data.slides.length > 0) {
            // Slides extracted successfully!
            setSlides(slidesResponse.data.slides);
            setFileType(slidesResponse.data.file_type);
            setOriginalFileUrl(`${API}/sessions/${newSessionId}/presentation-file`);
            
            if (slidesResponse.data.pdf_url) {
              setPdfUrl(`${API}${slidesResponse.data.pdf_url.replace('/api', '')}`);
              setViewMode('pdf');
            }
            
            processingComplete = true;
            setExtractionStatus('done');
            setStep(3);
            setLoading(false);
            toast.success(`Presentation ready! ${slidesResponse.data.total} slides extracted.`);
            break;
            
          } else if (slidesResponse.data.pdf_url || has_pdf) {
            // PDF available, try to view it
            const pdfUrl = slidesResponse.data.pdf_url 
              ? `${API}${slidesResponse.data.pdf_url.replace('/api', '')}`
              : `${API}/sessions/${newSessionId}/pdf-view`;
            setPdfUrl(pdfUrl);
            setViewMode('pdf');
            setFileType('pdf');
            setOriginalFileUrl(`${API}/sessions/${newSessionId}/presentation-file`);
            
            // Check one more time for image slides
            await new Promise(resolve => setTimeout(resolve, 2000));
            const retryResponse = await axios.get(`${API}/sessions/${newSessionId}/slides`);
            
            if (retryResponse.data.slides && retryResponse.data.slides.length > 0) {
              setSlides(retryResponse.data.slides);
              toast.success(`${retryResponse.data.total} slides ready!`);
            } else {
              toast.success("PDF converted! Preview available.");
            }
            
            processingComplete = true;
            setExtractionStatus('done');
            setStep(3);
            setLoading(false);
            break;
          }
          
          // Update status message based on processing status
          if (status === 'converting') {
            setExtractionStatus('converting');
          } else if (status === 'extracting') {
            setExtractionStatus('extracting');
          }
          
        } catch (pollError) {
          console.log(`Poll attempt ${pollAttempts + 1} failed:`, pollError);
        }
        
        pollAttempts++;
      }
      
      // If still not ready after polling, try manual reconversion
      if (!processingComplete) {
        toast.warning("Slide extraction is taking longer than expected. Attempting retry...");
        
        try {
          // Try to trigger reconversion
          await axios.post(`${API}/sessions/${newSessionId}/reconvert-presentation`);
          
          // Wait a bit and check again
          await new Promise(resolve => setTimeout(resolve, 5000));
          
          const finalResponse = await axios.get(`${API}/sessions/${newSessionId}/slides`);
          
          if (finalResponse.data.slides && finalResponse.data.slides.length > 0) {
            setSlides(finalResponse.data.slides);
            setExtractionStatus('done');
            setStep(3);
            setLoading(false);
            toast.success(`Slides ready! ${finalResponse.data.total} slides extracted.`);
          } else {
            // Last resort - show preview page but with warning
            setFileType(finalResponse.data.file_type);
            if (finalResponse.data.pdf_url) {
              setPdfUrl(`${API}${finalResponse.data.pdf_url.replace('/api', '')}`);
              setViewMode('pdf');
            }
            setExtractionStatus('done');
            setStep(3);
            setLoading(false);
            toast.warning("Slide preview may be limited. You can retry or proceed with your session.");
          }
        } catch (retryError) {
          console.error("Retry conversion failed:", retryError);
          setExtractionStatus('failed');
          setStep(3);
          setLoading(false);
          toast.error("Slide extraction failed. Please try uploading a different file format.");
        }
      }

    } catch (error) {
      console.error("Session creation failed:", error);
      setExtractionStatus('failed');
      toast.error(error.response?.data?.detail || "Failed to process presentation");
      setLoading(false);
    }
  };

  // PDF.js callbacks
  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
  }, []);

  const changePage = (offset) => {
    setPageNumber(prevPageNumber => Math.min(Math.max(1, prevPageNumber + offset), numPages || 1));
  };

  const zoomIn = () => setPdfScale(prev => Math.min(prev + 0.2, 2.5));
  const zoomOut = () => setPdfScale(prev => Math.max(prev - 0.2, 0.5));

  const retryExtraction = async () => {
    if (!sessionId) return;
    setExtractionStatus('extracting');
    setLoading(true);
    
    try {
      const slidesResponse = await axios.get(`${API}/sessions/${sessionId}/slides`);
      
      // Update file info
      if (slidesResponse.data.file_type) {
        setFileType(slidesResponse.data.file_type);
        setOriginalFileUrl(`${API}/sessions/${sessionId}/presentation-file`);
        
        // Use pdf_url for viewing if available (handles both original PDFs and converted PPTs)
        if (slidesResponse.data.pdf_url) {
          setPdfUrl(`${API}${slidesResponse.data.pdf_url.replace('/api', '')}`);
          setViewMode('pdf');
        } else if (slidesResponse.data.file_type === 'pdf') {
          setPdfUrl(`${API}/sessions/${sessionId}/presentation-file`);
          setViewMode('pdf');
        }
      }
      
      if (slidesResponse.data.slides && slidesResponse.data.slides.length > 0) {
        setSlides(slidesResponse.data.slides);
        setExtractionStatus('done');
        setStep(3);
        toast.success(`${slidesResponse.data.total} slides ready.`);
      } else {
        setExtractionStatus('done');
        setStep(3);
        toast.warning("Slide preview not available.");
      }
    } catch (error) {
      setExtractionStatus('failed');
      toast.error("Failed to extract slides");
    } finally {
      setLoading(false);
    }
  };

  const startSession = () => {
    if (sessionId) {
      navigate(`/session/${sessionId}/live`);
    }
  };

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  // Step 1: Form
  if (step === 1) {
    return (
      <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Create EVOLVE Session
          </h1>
          <p className="text-[#5C5C5C] mt-1">
            Step 1 of 3: Enter session details and upload your presentation
          </p>
        </div>

        {/* Progress indicator */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-medium">1</div>
            <span className="text-sm font-medium text-primary">Details</span>
          </div>
          <div className="flex-1 h-1 bg-border rounded" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-border text-[#5C5C5C] flex items-center justify-center text-sm font-medium">2</div>
            <span className="text-sm text-[#5C5C5C]">Process</span>
          </div>
          <div className="flex-1 h-1 bg-border rounded" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-border text-[#5C5C5C] flex items-center justify-center text-sm font-medium">3</div>
            <span className="text-sm text-[#5C5C5C]">Preview</span>
          </div>
        </div>

        <Card className="stats-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Presentation className="w-5 h-5 text-primary" />
              Session Details
            </CardTitle>
            <CardDescription>
              Fill in the details for your presentation session
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="user_id">Presenter</Label>
                <Select
                  value={formData.user_id}
                  onValueChange={(value) => setFormData({ ...formData, user_id: value })}
                >
                  <SelectTrigger data-testid="presenter-select">
                    <SelectValue placeholder="Select presenter" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name} - {user.department}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="topic_name">Presentation Topic *</Label>
                <Input
                  id="topic_name"
                  data-testid="topic-input"
                  placeholder="e.g., Q4 Sales Strategy Review"
                  value={formData.topic_name}
                  onChange={(e) => setFormData({ ...formData, topic_name: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="concept_overview">Concept Overview *</Label>
                <Textarea
                  id="concept_overview"
                  data-testid="overview-input"
                  placeholder="Brief description of what you'll be presenting..."
                  value={formData.concept_overview}
                  onChange={(e) => setFormData({ ...formData, concept_overview: e.target.value })}
                  rows={3}
                  required
                />
              </div>

              <div>
                <Label htmlFor="notes">Additional Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  data-testid="notes-input"
                  placeholder="Any specific areas you want feedback on..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                />
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  Presentation File *
                  <span className="text-xs text-accent font-normal">(Required)</span>
                </Label>
                <div 
                  className={`mt-2 border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
                    presentationFile ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => document.getElementById('file-upload').click()}
                >
                  <input
                    id="file-upload"
                    type="file"
                    className="hidden"
                    accept=".ppt,.pptx,.pdf,.key,.odp,.odf,.pptm"
                    onChange={handleFileChange}
                    data-testid="file-upload"
                  />
                  {presentationFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileText className="w-8 h-8 text-primary" />
                      <div className="text-left">
                        <p className="font-medium text-[#1A1A1A]">{presentationFile.name}</p>
                        <p className="text-sm text-[#5C5C5C]">
                          {(presentationFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      <CheckCircle className="w-5 h-5 text-[#059669]" />
                    </div>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                      <p className="text-[#5C5C5C]">Click to upload your presentation</p>
                      <p className="text-sm text-[#9CA3AF] mt-1">PPT, PPTX, PDF files supported</p>
                    </>
                  )}
                </div>
                
                {!presentationFile && (
                  <div className="flex items-center gap-2 mt-2 p-3 bg-[#FEF3C7] rounded-lg">
                    <AlertCircle className="w-4 h-4 text-[#D97706]" />
                    <p className="text-sm text-[#92400E]">
                      A presentation file is required to continue
                    </p>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end pt-4 border-t border-border">
                <Button
                  type="submit"
                  data-testid="upload-and-process-btn"
                  disabled={!presentationFile || !formData.topic_name}
                  className="bg-primary hover:bg-primary/90 rounded-full px-8"
                >
                  Upload & Process
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 2: Processing
  if (step === 2) {
    return (
      <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Processing Presentation
          </h1>
          <p className="text-[#5C5C5C] mt-1">
            Step 2 of 3: Extracting slides from your presentation
          </p>
        </div>

        {/* Progress indicator */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#059669] text-white flex items-center justify-center">
              <CheckCircle className="w-4 h-4" />
            </div>
            <span className="text-sm text-[#059669]">Details</span>
          </div>
          <div className="flex-1 h-1 bg-primary rounded" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-medium">2</div>
            <span className="text-sm font-medium text-primary">Process</span>
          </div>
          <div className="flex-1 h-1 bg-border rounded" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-border text-[#5C5C5C] flex items-center justify-center text-sm font-medium">3</div>
            <span className="text-sm text-[#5C5C5C]">Preview</span>
          </div>
        </div>

        <Card className="stats-card">
          <CardContent className="py-16">
            <div className="text-center space-y-6">
              {extractionStatus === 'uploading' && (
                <>
                  <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto" />
                  <div>
                    <h2 className="text-xl font-semibold text-[#1A1A1A]">Uploading Presentation</h2>
                    <p className="text-[#5C5C5C] mt-1">Please wait while we upload your file...</p>
                  </div>
                </>
              )}
              
              {extractionStatus === 'extracting' && (
                <>
                  <div className="relative">
                    <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-8 h-8 bg-primary/10 rounded-full animate-pulse" />
                    </div>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-[#1A1A1A]">Converting & Extracting Slides</h2>
                    <p className="text-[#5C5C5C] mt-1">Please wait while we process your presentation...</p>
                    <p className="text-sm text-[#9CA3AF] mt-2">
                      This may take up to 60 seconds for PPT/PPTX files
                    </p>
                    <div className="mt-4 w-48 mx-auto">
                      <div className="h-1 bg-primary/20 rounded-full overflow-hidden">
                        <div className="h-full bg-primary rounded-full animate-[pulse_2s_ease-in-out_infinite] w-3/4" />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {extractionStatus === 'failed' && (
                <>
                  <AlertCircle className="w-16 h-16 text-[#DC2626] mx-auto" />
                  <div>
                    <h2 className="text-xl font-semibold text-[#1A1A1A]">Processing Failed</h2>
                    <p className="text-[#5C5C5C] mt-1">There was an error processing your presentation</p>
                  </div>
                  <div className="flex justify-center gap-4">
                    <Button variant="outline" onClick={() => setStep(1)} className="rounded-full">
                      Go Back
                    </Button>
                    <Button onClick={retryExtraction} className="bg-primary hover:bg-primary/90 rounded-full">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Retry
                    </Button>
                  </div>
                </>
              )}

              {/* File info */}
              <div className="flex items-center justify-center gap-3 p-4 bg-secondary/50 rounded-xl max-w-sm mx-auto">
                <FileText className="w-8 h-8 text-primary" />
                <div className="text-left">
                  <p className="font-medium text-[#1A1A1A] truncate max-w-[200px]">{presentationFile?.name}</p>
                  <p className="text-sm text-[#5C5C5C]">
                    {presentationFile && (presentationFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 3: Preview
  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Preview Your Presentation
        </h1>
        <p className="text-[#5C5C5C] mt-1">
          Step 3 of 3: Review your slides and start when ready
        </p>
      </div>

      {/* Progress indicator */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#059669] text-white flex items-center justify-center">
            <CheckCircle className="w-4 h-4" />
          </div>
          <span className="text-sm text-[#059669]">Details</span>
        </div>
        <div className="flex-1 h-1 bg-[#059669] rounded" />
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#059669] text-white flex items-center justify-center">
            <CheckCircle className="w-4 h-4" />
          </div>
          <span className="text-sm text-[#059669]">Process</span>
        </div>
        <div className="flex-1 h-1 bg-primary rounded" />
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-medium">3</div>
          <span className="text-sm font-medium text-primary">Preview</span>
        </div>
      </div>

      {/* Session Info */}
      <Card className="stats-card">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-[#1A1A1A]">{formData.topic_name}</h2>
              <p className="text-sm text-[#5C5C5C]">{formData.concept_overview}</p>
            </div>
            <div className="flex items-center gap-3">
              {/* Replace Presentation Button */}
              <div className="relative">
                <input
                  id="replace-file"
                  type="file"
                  className="hidden"
                  accept=".ppt,.pptx,.pdf,.key,.odp,.odf,.pptm"
                  onChange={handleReplaceFile}
                />
                <Button
                  variant="outline"
                  size="sm"
                  disabled={replacingFile}
                  onClick={() => document.getElementById('replace-file').click()}
                  className="rounded-full"
                >
                  {replacingFile ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Replacing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Replace PPT
                    </>
                  )}
                </Button>
              </div>
              <div className="flex items-center gap-2 text-sm text-[#059669]">
                <CheckCircle className="w-4 h-4" />
                Ready to present
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error State with Retry */}
      {extractionStatus === 'error' && (
        <Card className="stats-card border-red-200 bg-red-50">
          <CardContent className="py-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-red-800">Processing Failed</h3>
                <p className="text-sm text-red-600">
                  The presentation could not be processed. This may be due to a conversion issue or unsupported file format.
                </p>
              </div>
              <Button
                onClick={async () => {
                  try {
                    setExtractionStatus('extracting');
                    toast.info("Retrying processing...");
                    await axios.post(`${API}/sessions/${sessionId}/retry-processing`);
                    
                    // Poll for completion
                    let attempts = 0;
                    while (attempts < 30) {
                      await new Promise(r => setTimeout(r, 2000));
                      const status = await axios.get(`${API}/sessions/${sessionId}/processing-status`);
                      if (status.data.status === 'complete') {
                        const slidesRes = await axios.get(`${API}/sessions/${sessionId}/slides`);
                        if (slidesRes.data.slides?.length > 0) {
                          setSlides(slidesRes.data.slides);
                        }
                        if (slidesRes.data.pdf_url) {
                          setPdfUrl(`${API}${slidesRes.data.pdf_url.replace('/api', '')}`);
                          setViewMode('pdf');
                        }
                        setExtractionStatus('done');
                        toast.success("Processing complete!");
                        break;
                      } else if (status.data.status === 'failed') {
                        setExtractionStatus('error');
                        toast.error(status.data.error || "Processing failed again");
                        break;
                      }
                      attempts++;
                    }
                  } catch (err) {
                    toast.error("Retry failed: " + (err.response?.data?.detail || err.message));
                    setExtractionStatus('error');
                  }
                }}
                className="rounded-full bg-red-600 hover:bg-red-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry Processing
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Slide/PDF Preview */}
      <Card className="stats-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold">
              {fileType === 'pdf' ? 'PDF Preview' : 'Slide Preview'} 
              {fileType === 'pdf' && numPages && ` (${numPages} pages)`}
              {fileType !== 'pdf' && slides.length > 0 && ` (${slides.length} slides)`}
            </CardTitle>
            <div className="flex items-center gap-2">
              {fileType === 'pdf' && (
                <>
                  <Button variant="outline" size="sm" onClick={zoomOut} className="rounded-full">
                    <ZoomOut className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground">{Math.round(pdfScale * 100)}%</span>
                  <Button variant="outline" size="sm" onClick={zoomIn} className="rounded-full">
                    <ZoomIn className="w-4 h-4" />
                  </Button>
                </>
              )}
              {originalFileUrl && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => window.open(originalFileUrl, '_blank')}
                  className="rounded-full"
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Open Full
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Image Slides Viewer - Preferred over PDF for reliability */}
          {slides.length > 0 && (
            <div className="space-y-4">
              {/* Main slide view */}
              <div className="aspect-video bg-white rounded-xl border-2 border-border overflow-hidden relative">
                <img 
                  src={`${process.env.REACT_APP_BACKEND_URL}${slides[currentSlide]}`}
                  alt={`Slide ${currentSlide + 1}`}
                  className="w-full h-full object-contain"
                  data-testid="preview-slide"
                  onError={(e) => {
                    console.log("Slide image failed to load:", slides[currentSlide]);
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><text x="50%" y="50%" text-anchor="middle" fill="gray">Loading...</text></svg>';
                  }}
                />
                <div className="absolute bottom-4 right-4 px-3 py-1 bg-primary/90 rounded-full text-sm text-white font-medium">
                  {currentSlide + 1} / {slides.length}
                </div>
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-center gap-4">
                <Button
                  variant="outline"
                  onClick={prevSlide}
                  disabled={currentSlide === 0}
                  className="rounded-full"
                >
                  <ChevronLeft className="w-5 h-5" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={nextSlide}
                  disabled={currentSlide === slides.length - 1}
                  className="rounded-full"
                >
                  Next
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>

              {/* Thumbnails */}
              <div className="flex gap-2 justify-center overflow-x-auto pb-2">
                {slides.map((slide, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentSlide(idx)}
                    className={`flex-shrink-0 w-20 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                      idx === currentSlide ? 'border-primary ring-2 ring-primary/20' : 'border-border opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}${slide}`}
                      alt={`Thumbnail ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* PDF Viewer - Fallback when no slide images available */}
          {slides.length === 0 && fileType === 'pdf' && pdfUrl && (
            <div className="space-y-4">
              <div className="bg-slate-100 rounded-xl overflow-hidden" style={{height: '500px'}}>
                <iframe 
                  src={pdfUrl}
                  title="Presentation Preview"
                  className="w-full h-full border-0"
                />
              </div>
              <div className="flex items-center justify-center">
                <Button
                  variant="outline"
                  onClick={() => window.open(pdfUrl, '_blank')}
                  className="rounded-full"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open PDF in New Tab
                </Button>
              </div>
            </div>
          )}

          {/* No slides and not PDF - show external viewer option */}
          {!slides.length && fileType !== 'pdf' && (
            <div className="py-12 text-center space-y-4">
              <AlertCircle className="w-12 h-12 text-[#D97706] mx-auto" />
              <div>
                <h3 className="font-semibold text-[#1A1A1A]">Slide preview not available</h3>
                <p className="text-sm text-[#5C5C5C] mt-1">
                  PPT/PPTX files require conversion for preview. You can still start your session.
                </p>
              </div>
              {originalFileUrl && (
                <div className="flex justify-center gap-3">
                  <Button 
                    variant="outline"
                    onClick={() => window.open(originalFileUrl, '_blank')}
                    className="rounded-full"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Original
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      // Use Google Docs viewer for PPT files
                      const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(originalFileUrl)}&embedded=true`;
                      window.open(viewerUrl, '_blank');
                    }}
                    className="rounded-full"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open in Google Docs
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action buttons */}
      <div className="flex items-center justify-between">
        <Button 
          variant="outline" 
          onClick={() => {
            setStep(1);
            setSessionId(null);
            setSlides([]);
            setNumPages(null);
            setPageNumber(1);
            setFileType(null);
            setOriginalFileUrl(null);
          }}
          className="rounded-full"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Start Over
        </Button>
        <Button
          onClick={startSession}
          data-testid="start-session-btn"
          className="bg-[#059669] hover:bg-[#059669]/90 rounded-full px-8 py-6 text-lg"
        >
          <Play className="w-5 h-5 mr-2" />
          Start Presentation
        </Button>
      </div>
    </div>
  );
}
